
import i18n from '../i18n';// reportClientError.js
//
// Remontée des erreurs du navigateur vers la file `api_incidents`, d'où le cron
// `apiIncidentAlerts.php` envoie un mail au-delà de 5 occurrences récentes.
//
// Deux appelants :
//   - `ErrorBoundary`, pour les exceptions levées pendant un rendu React ;
//   - `installGlobalErrorReporting()`, pour ce qui échappe à React — promesses
//     rejetées sans `.catch()` et erreurs hors composant.
//
// DONNÉE TECHNIQUE UNIQUEMENT : message, forme de la route, version du build.
// Jamais d'identifiant d'utilisateur, jamais de parcours de navigation. Le
// serveur normalise en plus la route (`/groupe/482` -> `/groupe/:id`) pour
// qu'aucun identifiant de classe ne se retrouve en base.

import { isChunkLoadError, ongletPerime } from './chunkObsolete';

const ENDPOINT ='/serveur/db/reportClientError.php';

/**
 * Erreurs à NE PAS remonter.
 *
 * Le seuil d'alerte est de 5 occurrences récentes : si le bruit de fond réseau
 * remontait, le mail partirait en permanence et cesserait d'être lu. On ne garde
 * que ce qui ressemble à un défaut de l'application.
 *
 * - `AbortError` : requête annulée volontairement (changement de page, bouton
 *   « arrêter la génération »). C'est un fonctionnement normal.
 * - Échecs réseau purs : un élève sur un wifi d'établissement instable en
 *   produit à la chaîne, et ils ne disent rien sur notre code.
 * - `ResizeObserver loop…` : bruit connu des navigateurs, sans conséquence.
 * - Code que le navigateur ou une extension injecte dans la page : passerelle
 *   native d'un navigateur intégré (« Object Not Found Matching Id:5,
 *   MethodName:update, ParamCount:4 », 18 fois en trois secondes le 06/10/2026
 *   depuis un seul client), portefeuille crypto (`window.ethereum`), extension
 *   (`runtime.sendMessage`, « No Listener: tabs:… »). Le 12/10/2026, un seul
 *   client a remonté 99 fois « Cannot read properties of undefined (reading
 *   'M_ID') » en quatre heures : `M_ID` n'existe nulle part dans le bundle
 *   déployé. Aucune de ces erreurs ne porte d'image de pile de
 *   notre build, ni de version : elles ne viennent pas de notre code. Même liste
 *   côté serveur (`clientErrorFilter.php`), pour les onglets restés sur un
 *   ancien bundle.
 */
function estDuBruit(error) {
  if (!error) return true;

  if (error.name === 'AbortError' || error.name === 'CanceledError') return true;

  const message = String(error.message || error || '');

  return (
    /Failed to fetch/i.test(message) ||
    /NetworkError/i.test(message) ||
    /Load failed/i.test(message) ||               // Safari, réseau coupé
    /ResizeObserver loop/i.test(message) ||
    /The operation was aborted/i.test(message) ||
    /Object Not Found Matching Id:\d+, MethodName:/i.test(message) ||
    /window\.ethereum/i.test(message) ||
    /Invalid call to runtime\.sendMessage/i.test(message) ||
    /No Listener: tabs:/i.test(message) ||        // messagerie d'extension (Chrome)
    /\bM_ID\b/.test(message) ||                   // propriété d'un script injecté, absente de notre build
    /SCDynimacBridge/.test(message)               // passerelle d'un navigateur intégré (Safari)
  );
}

/**
 * Première image de la pile qui désigne un fichier du build, réduite à
 * `NomDuChunk-hash.js:ligne:colonne`.
 *
 * POURQUOI SEULEMENT ÇA
 *
 * Un message seul ne se diagnostique pas. « Cannot read properties of null
 * (reading '4') », remonté trois fois depuis /chatbot/:id/preview les 18 et
 * 19/08/2026, ne désigne aucun fichier : impossible de savoir quoi corriger, et
 * une recherche dans les sources ne donne rien. Avec cette seule ligne, la
 * sourcemap du build (`build/assets/*.js.map`, générée en production) rend le
 * fichier et la ligne d'origine.
 *
 * Ce qui est gardé est un nom de fichier de NOTRE bundle et deux nombres. Ni
 * origine, ni paramètres de requête, ni chemin de navigation — la pile complète
 * en contiendrait, la règle du fichier reste la même : de la donnée technique,
 * et rien qui désigne quelqu'un.
 */
function frameTechnique(error) {
  const pile = String((error && error.stack) || '');
  const m = pile.match(/\/assets\/([A-Za-z0-9._-]+):(\d+):(\d+)/);
  return m ? `${m[1]}:${m[2]}:${m[3]}` : '';
}

/**
 * Envoie l'erreur au serveur. Ne lève jamais : un rapporteur d'erreur qui échoue
 * ne doit pas produire une seconde erreur, ni empêcher l'écran de secours de
 * s'afficher.
 */
export async function reportClientError(error) {
  try {
    if (estDuBruit(error)) return;

    // Un chunk disparu parce qu'un deploiement a remplace le build est le cas
    // NORMAL d'un onglet reste ouvert : l'ErrorBoundary recharge, rien a signaler.
    if (isChunkLoadError(error) && (await ongletPerime())) return;

    const body = JSON.stringify({
      message: String(error?.message || error || 'erreur inconnue').slice(0, 400),
      route: window.location.pathname,
      version: (typeof process !== 'undefined' && process.env && process.env.REACT_APP_VERSION) || '',
      frame: frameTechnique(error),
    });

    // `fetch` brut plutôt qu'apiClient : celui-ci ajoute des en-têtes CSRF et une
    // logique de reprise dont on ne veut pas ici — et il pourrait être lui-même
    // en cause.
    //
    // `keepalive` : la requête doit survivre au rechargement que l'ErrorBoundary
    // déclenche juste après sur un chunk obsolète.
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'include',
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* jamais d'erreur secondaire depuis le rapporteur d'erreur */
  }
}

/**
 * Branche les gestionnaires globaux.
 *
 * L'ErrorBoundary ne voit que ce qui casse PENDANT un rendu React. Tout le reste
 * lui échappe : une promesse rejetée dans un gestionnaire d'événement, une erreur
 * dans un `setTimeout`, un échec de traitement audio. Ce sont pourtant des
 * défauts réels, et jusqu'ici ils ne laissaient aucune trace exploitable.
 *
 * Appelé une seule fois, au démarrage (`src/index.js`).
 */
export function installGlobalErrorReporting() {
  if (typeof window === 'undefined' || window.__c1bErrorReportingInstalled) return;
  window.__c1bErrorReportingInstalled = true;

  window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event?.reason);
  });

  window.addEventListener('error', (event) => {
    // `error` couvre aussi les échecs de chargement de ressources (balises img,
    // script), qui n'ont pas d'objet Error : on les ignore, ils ne disent rien
    // sur la santé du code.
    if (event?.error) {
      reportClientError(event.error);
    }
  });
}
